import React from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { ServiceError } from '@/types/service';
import { capitalizeFirstLetter } from '@/utilities/string';
import styles from './Error.module.scss';

export const ServiceErrorView: React.FC<{ error: ServiceError; useOriginMsg?: boolean }> = ({
  error,
  useOriginMsg,
}) => {
  const { t } = useTranslation();
  switch (error.kind) {
    case 'QueryError':
      return <ErrorView message={t('service_error.general')} />;
    case 'NetworkingError':
      return <ErrorView message={t('service_error.networking')} />;
    case 'InvalidData':
      return <ErrorView message={t('service_error.invalid_data')} />;
    case 'ServerError':
      return (
        <ErrorView
          message={error.message ? toLocalMessage(t, error.message, useOriginMsg) : t('service_error.general')}
        />
      );
  }
};

export const ErrorView: React.FC<{ message: string }> = ({ message }) => {
  return <div className={styles.error}>{message}</div>;
};

function toLocalMessage(t: TFunction, message: string, useOriginMsg = false): string {
  switch (message) {
    case 'PRODUCT_NOT_FOUND':
    case 'PRODUCT_MISSING_INPUT_ERROR':
      return t('item.not_found');
    case 'ORDER_NOT_FOUND':
      return t('order.not_found');
    case 'CUSTOMER_NOT_FOUND':
      return t('client.not_found');
    default:
      if (message.search('No route found for') !== -1) {
        return t('service_error.route_not_found');
      }
      return useOriginMsg ? message : removeUnnecessaryServerMessage(t, message);
  }
}

function removeUnnecessaryServerMessage(t: TFunction, message: string): string {
  const messages = ['Internal Server Error - '];
  const replace = (mx: string[], m: string): string => {
    const [r, ...rest] = mx;
    return r ? replace(rest, m.replace(r, '')) : m;
  };

  return capitalizeFirstLetter(replace(messages, message)) || t('service_error.general');
}
